// Subscribes to every OPEN contract for the current account and surfaces
// live P&L snapshots. Uses Deriv's documented two-step pattern:
//   1) `portfolio: 1` → list of open contract_ids
//   2) `proposal_open_contract: 1 + contract_id + subscribe: 1` per contract
//
// IMPORTANT (v8.9.223): the authenticated socket runs on Deriv's NEW options
// trading endpoint (`/trading/v1/options/ws/`), where `proposal_open_contract`
// streaming is unreliable — the first snapshot arrives but live ticks often
// never stream, leaving rows stuck on "syncing…" with no P&L. So in ADDITION
// to the subscription we POLL a one-shot `proposal_open_contract` for every
// open contract every few seconds; that guarantees fresh P&L (and settlement
// detection) even when the stream is silent.
//
// Returns:
//   { contracts: [...], sellContract(id), refresh }
import { useEffect, useRef, useState, useCallback } from "react";
import { subscribe, forget, send } from "@/lib/deriv";

const snapshotFrom = (c) => {
  const buy = parseFloat(c.buy_price ?? 0);
  // bid_price is sometimes absent or 0 on the FIRST portfolio response
  // (before proposal_open_contract has streamed even one tick). Treat
  // undefined and 0 as "pending" so we don't compute a misleading
  // -100% loss on what is actually a live winning trade.
  const rawBid = c.bid_price;
  const hasBid = rawBid !== undefined && rawBid !== null && rawBid !== "" && parseFloat(rawBid) > 0;
  const bid = hasBid ? parseFloat(rawBid) : null;
  // Prefer Deriv's authoritative `profit` field. Only fall back to
  // (bid - buy) when we have a *real* bid; never compute profit from
  // a missing bid_price (that produced the false −100% in the ribbon).
  const hasProfit = c.profit !== undefined && c.profit !== null && c.profit !== "";
  let profit = null;
  if (hasProfit) profit = parseFloat(c.profit);
  else if (bid != null) profit = bid - buy;
  // pending = we don't yet know the live P&L — keep the row visible but
  // hide the misleading numbers until the stream produces real data.
  const pending = profit == null;
  const profit_pct = !pending && buy > 0 ? (profit / buy) * 100 : 0;
  return {
    contract_id: String(c.contract_id),
    symbol: c.underlying || c.underlying_symbol || c.symbol || "",
    display_name: c.display_name || "",
    contract_type: c.contract_type || "",
    buy_price: buy,
    bid_price: bid != null ? bid : buy, // best-effort fallback for UI text
    payout: parseFloat(c.payout ?? 0),
    profit: profit || 0,
    profit_pct,
    pending,
    is_sold: !!c.is_sold,
    status: c.status || (c.is_sold ? "closed" : "open"),
    longcode: c.longcode || "",
    multiplier: c.multiplier ? Number(c.multiplier) : null,
    purchase_time: c.purchase_time || c.date_start || 0,
  };
};

export const useActiveContracts = (authorized) => {
  const [contracts, setContracts] = useState([]);
  const mapRef = useRef(new Map()); // contract_id → snapshot
  const subIdsRef = useRef(new Map()); // contract_id → subscription_id
  const settledRef = useRef(new Set()); // contract_ids we already finalized
  const pollRef = useRef(null);
  const pnlPollRef = useRef(null);
  const refreshingRef = useRef(false);
  const pnlBusyRef = useRef(false);

  const publish = useCallback(() => {
    setContracts(
      Array.from(mapRef.current.values()).sort((a, b) => b.purchase_time - a.purchase_time)
    );
  }, []);

  // Single place that ingests a proposal_open_contract payload (from the
  // stream OR the poll) and updates state + handles settlement once.
  const applyContract = useCallback((c) => {
    if (!c?.contract_id) return;
    const cid = String(c.contract_id);
    const snap = snapshotFrom(c);
    const isSettled = snap.is_sold || ["sold", "lost", "won"].includes(snap.status);
    mapRef.current.set(cid, snap);
    publish();
    if (isSettled && !settledRef.current.has(cid)) {
      settledRef.current.add(cid);
      // Notify the rest of the app a contract just settled so the bot stats
      // panel (and anything else) can update without re-polling.
      try {
        window.dispatchEvent(
          new CustomEvent("vts:contract-closed", {
            detail: {
              contract_id: cid,
              profit: snap.profit,
              won: snap.profit >= 0,
              symbol: snap.symbol,
              contract_type: snap.contract_type,
            },
          })
        );
      } catch { /* ignore */ }
      // PATCH the trade record so History reflects the outcome.
      import("@/lib/api").then(({ api }) => {
        api.patch(`/track/trade/${cid}`, {
          status: snap.profit >= 0 ? "won" : "lost",
          profit: snap.profit,
        }).catch(() => { /* ignore */ });
      }).catch(() => { /* ignore */ });
      // Show the final tick for 4s, then drop + unsubscribe.
      setTimeout(() => {
        mapRef.current.delete(cid);
        const sid = subIdsRef.current.get(cid);
        if (sid != null) {
          forget(sid);
          subIdsRef.current.delete(cid);
        }
        publish();
      }, 4000);
    }
  }, [publish]);

  const subscribeContract = useCallback(
    async (contract_id) => {
      const cid = String(contract_id);
      if (subIdsRef.current.has(cid)) return;
      try {
        const sub = await subscribe(
          { proposal_open_contract: 1, contract_id: cid },
          (msg) => applyContract(msg?.proposal_open_contract)
        );
        subIdsRef.current.set(cid, sub.subscription_id);
        // Seed immediately from the first response so the row appears with
        // real numbers (not "—") even before the next stream tick.
        if (sub?.first?.proposal_open_contract) {
          applyContract(sub.first.proposal_open_contract);
        }
      } catch {
        /* swallowed — refreshPortfolio / pollPnl will retry */
      }
    },
    [applyContract]
  );

  const refreshPortfolio = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const res = await send({ portfolio: 1 });
      const list = res?.portfolio?.contracts || [];
      const liveIds = new Set();
      for (const c of list) {
        const cid = String(c.contract_id);
        liveIds.add(cid);
        // Seed if we don't have a snapshot yet so the ribbon shows something
        // immediately while we wait for the proposal_open_contract data.
        if (!mapRef.current.has(cid)) {
          mapRef.current.set(cid, snapshotFrom(c));
          publish();
        }
        await subscribeContract(cid);
      }
      // Drop any tracked contracts that are no longer in the portfolio AND not
      // mid-settlement-animation (settled ones are removed by applyContract's
      // 4s timer; this catches contracts that vanished while we were offline).
      for (const cid of Array.from(mapRef.current.keys())) {
        if (!liveIds.has(cid) && !settledRef.current.has(cid)) {
          const sid = subIdsRef.current.get(cid);
          if (sid != null) forget(sid);
          subIdsRef.current.delete(cid);
          mapRef.current.delete(cid);
        }
      }
      publish();
    } catch {
      /* socket may be reconnecting; next poll will catch up */
    } finally {
      refreshingRef.current = false;
    }
  }, [publish, subscribeContract]);

  // Poll live P&L via one-shot proposal_open_contract for every tracked open
  // contract. This is the reliability backstop for the new options endpoint
  // where the subscription stream often goes silent (the "stuck on syncing"
  // bug). One-shot POC always returns the authoritative `profit`.
  const pollPnl = useCallback(async () => {
    if (pnlBusyRef.current) return;
    const ids = Array.from(mapRef.current.values())
      .filter((s) => s && !s.is_sold)
      .map((s) => s.contract_id);
    if (ids.length === 0) return;
    pnlBusyRef.current = true;
    try {
      await Promise.all(
        ids.map(async (cid) => {
          try {
            const res = await send({ proposal_open_contract: 1, contract_id: cid });
            const c = res?.proposal_open_contract;
            if (c?.contract_id) applyContract(c);
          } catch { /* ignore individual failures */ }
        })
      );
    } finally {
      pnlBusyRef.current = false;
    }
  }, [applyContract]);

  useEffect(() => {
    if (!authorized) {
      // Tear down
      subIdsRef.current.forEach((sid) => forget(sid));
      subIdsRef.current.clear();
      mapRef.current.clear();
      settledRef.current.clear();
      setContracts([]);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (pnlPollRef.current) { clearInterval(pnlPollRef.current); pnlPollRef.current = null; }
      return undefined;
    }
    refreshPortfolio();
    pollRef.current = setInterval(refreshPortfolio, 10000); // discover new/closed
    pnlPollRef.current = setInterval(pollPnl, 3000);        // keep P&L live
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (pnlPollRef.current) { clearInterval(pnlPollRef.current); pnlPollRef.current = null; }
      subIdsRef.current.forEach((sid) => forget(sid));
      subIdsRef.current.clear();
      mapRef.current.clear();
      settledRef.current.clear();
    };
  }, [authorized, refreshPortfolio, pollPnl]);

  const sellContract = async (contract_id) => {
    try {
      const res = await send({ sell: String(contract_id), price: 0 });
      if (res?.error) throw new Error(res.error.message);
      return { ok: true, sold_for: parseFloat(res?.sell?.sold_for ?? 0) };
    } catch (e) {
      return { ok: false, error: e?.message || "Sell failed" };
    }
  };

  return { contracts, sellContract, refresh: refreshPortfolio };
};
